/**
 * Stage 6: SEO Validation — context-based version using StagePanel.
 *
 * Runs SEO quality checks and displays a detailed scorecard.
 * Article editing goes through the document editor.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const validation = (doc.preflight as any)?.validation;
  if (!validation) {
    return <div className="text-sm text-gray-500">No validation results yet.</div>;
  }

  // The validation data may be at validation.validation_report or directly on validation
  const report = validation.validation_report || validation;
  const checks = Array.isArray(report.checks) ? report.checks : [];
  const overallStatus = report.overall_status || validation.overall_status;

  if (checks.length === 0) {
    return <div className="text-sm text-gray-500">No checks found in validation report.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Overall status banner */}
      <div className={`rounded-lg border p-4 ${
        overallStatus === 'pass' ? 'bg-green-50 border-green-200'
        : overallStatus === 'warn' ? 'bg-amber-50 border-amber-200'
        : 'bg-red-50 border-red-200'
      }`}>
        {overallStatus === 'pass' && (
          <p className="text-sm font-medium text-green-800">All checks passed. Article passed SEO validation.</p>
        )}
        {overallStatus === 'warn' && (
          <p className="text-sm font-medium text-amber-800">Warnings present. Review flagged items before proceeding.</p>
        )}
        {overallStatus === 'fail' && (
          <p className="text-sm font-medium text-red-800">Failures detected. Fix SEO issues below, then re-run or proceed.</p>
        )}
      </div>

      {/* Checks table */}
      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="text-left py-2 px-3 w-8">&mdash;</th>
              <th className="text-left py-2 px-3">Check</th>
              <th className="text-left py-2 px-3">Measured value</th>
              <th className="text-left py-2 px-3">Fix (if any)</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((c: any) => (
              <tr key={c.check_id} className="border-b border-gray-100">
                <td className="py-2 px-3">
                  {c.status === 'pass' ? '✅' : c.status === 'warn' ? '⚠️' : '❌'}
                </td>
                <td className="py-2 px-3 font-medium">{c.check_name}</td>
                <td className="py-2 px-3 text-gray-700">
                  {typeof c.measured_value === 'object' ? JSON.stringify(c.measured_value) : String(c.measured_value || '—')}
                </td>
                <td className="py-2 px-3 text-gray-600">{c.fix ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Expandable details per check */}
      <details className="text-xs text-gray-600">
        <summary className="cursor-pointer font-medium">Expand details per check</summary>
        <div className="mt-2 space-y-2">
          {checks.map((c: any) => (
            <details key={c.check_id} className="border rounded p-2 bg-gray-50">
              <summary className="cursor-pointer">
                {c.status === 'pass' ? '✅' : c.status === 'warn' ? '⚠️' : '❌'} {c.check_name}
              </summary>
              {c.check_id === '13' && Array.isArray(c.details?.comparisons) && (
                <div className="mt-2 space-y-3">
                  <div className="text-xs font-medium text-gray-700">FAQ heading match</div>
                  {c.details.comparisons.map((comp: any, idx: number) => (
                    <div key={idx} className="border-l-2 pl-2 border-gray-200">
                      <div className="text-xs text-gray-600 truncate" title={comp.planned_question}>{comp.planned_question}</div>
                      <div className="grid grid-cols-3 gap-2 items-start mt-1 text-xs">
                        <div>
                          <span className="font-medium text-gray-500">Planned:</span>
                          <span className="ml-1">{Array.isArray(comp.planned_words) ? comp.planned_words.join(', ') : '—'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Heading:</span>
                          <span className="ml-1">{Array.isArray(comp.h3_words) ? comp.h3_words.join(', ') : '—'}</span>
                        </div>
                        <div className="text-right">
                          <span className={`font-medium ${comp.matched ? 'text-green-600' : 'text-amber-600'}`}>
                            {((comp.overlap ?? 0) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {c.details != null && (c.check_id !== '13' || !Array.isArray(c.details?.comparisons)) && (
                <pre className="mt-1 text-xs whitespace-pre-wrap overflow-x-auto">
                  {typeof c.details === 'object' ? JSON.stringify(c.details, null, 2) : String(c.details)}
                </pre>
              )}
            </details>
          ))}
        </div>
      </details>
    </div>
  );
}

export function Stage6SeoV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);

  return (
    <StagePanel
      stageId="stage_6_seo_validation"
      title="Stage 6: SEO Validation"
      description="Runs code-based SEO checks on the article. Review the report, then edit via the document editor if needed."
      runDisabled={!hasMarkdown}
      runDisabledReason={!hasMarkdown ? 'Requires article markdown' : undefined}
      renderOutput={renderOutput}
    >
      {document?.article?.seo && (
        <div className="text-xs text-gray-600 bg-gray-50 rounded border p-2 space-y-1">
          <div><span className="text-gray-400">Title:</span> {document.article.seo.meta_title}</div>
          <div><span className="text-gray-400">Slug:</span> /{document.article.seo.slug}</div>
          <div><span className="text-gray-400">Primary keyword:</span> {document.targeting?.keywords?.primary?.keyword || '—'}</div>
        </div>
      )}
    </StagePanel>
  );
}
